import React, { useContext, useState, useEffect } from "react";
import { Button, Typography, CircularProgress, MenuItem } from "@mui/material";
import StyledTextField from "../../../common/StyledComponents/StyledTextField";
import AlertModal from "../../../common/alertModal/AlertModal";
import useAxios from "../../../../hooks/useAxios";
import "../index.css";
import { AuthContext } from "../../../../context/AuthContext";

const StudentForm = ({ handleSubmit }) => {
  const [formData, setFormData] = useState({
    full_name: "",
    nationality: "",
    address: "",
    family_id: "",
    school_id: "",
    teacher_id: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const { setStatus } = useContext(AuthContext);
  const axiosInstance = useAxios();

  const [families, setFamilies] = useState([]);
  const [schools, setSchools] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [selection, setSelection] = useState(""); // "family", "teacher", "school"
  const [alert, setAlert] = useState({
    open: false,
    title: "",
    message: "",
    type: "success", // 'success' or 'error'
  });

  // Fetch families, schools, and teachers on mount
  const fetchData = async () => {
    try {
      const response = await axiosInstance.get("/users/schoolsandteachers");
      if (response.data.success) {
        setFamilies(Object.values(response.data.families || {})); // Ensure families is an array
        setSchools(Array.isArray(response.data.schools) ? response.data.schools : []); // Ensure schools is an array
        setTeachers(Array.isArray(response.data.teachers) ? response.data.teachers : []); 
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
  };
  useEffect(() => {
    fetchData();
  }, []);
  
  useEffect(() => {
    console.log('families: ',families);
    console.log('schools: ',schools);
    console.log('teachers: ',teachers);
  }, [families, schools, teachers]);
  

  // Filter teachers when a school is selected
  useEffect(() => {
    if (formData.school_id) {
      const filtered = teachers.filter(
        (teacher) => teacher.teacher_profile?.school_id === parseInt(formData.school_id)
      );
      setFilteredTeachers(filtered);
    } else {
      setFilteredTeachers([]);
    }
  }, [formData.school_id, teachers]);

  const validate = () => {
    const newErrors = {};
    if (!formData.full_name.trim()) newErrors.full_name = "Full Name is required.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectionChange = (e) => {
    setSelection(e.target.value);
    setFormData((prev) => ({
      ...prev,
      family_id: "",
      school_id: "",
      teacher_id: "",
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      setLoading(true);
      try {
        const result = await handleSubmit(formData);
        if (result.success) {
          const userStatus = result.status;
          if (userStatus) {
            setStatus(userStatus);
          }
          setAlert({
            open: true,
            title: "Success",
            message: result.message,
            type: "success",
          });
        } else {
          setAlert({
            open: true,
            title: "Error",
            message: result.message || "Unexpected error occurred.",
            type: "error",
          });
        }
      } catch (error) {
        setAlert({
          open: true,
          title: "Error",
          message: error.response?.data?.message || "An unexpected error occurred. Please try again.",
          type: "error",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      <form onSubmit={onSubmit} className="profile-form">
        <Typography className="profile-form-title">
          You are creating a <strong>student</strong> account.
        </Typography>
        <StyledTextField
          label="Full Name"
          name="full_name"
          value={formData.full_name}
          onChange={handleChange}
          fullWidth
          margin="normal"
          error={!!errors.full_name}
          helperText={errors.full_name}
          required
        />
        <StyledTextField
          label="Nationality"
          name="nationality"
          value={formData.nationality}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <StyledTextField
          label="Address"
          name="address"
          value={formData.address}
          onChange={handleChange}
          fullWidth
          margin="normal"
        />
        <StyledTextField
          select
          label="Are you associated with a..."
          value={selection}
          onChange={handleSelectionChange}
          fullWidth
          margin="normal"
        >
          <MenuItem value="">Select an option</MenuItem>
          <MenuItem value="family">Family</MenuItem>
          <MenuItem value="teacher">Standalone Teacher</MenuItem>
          <MenuItem value="school">School</MenuItem>
        </StyledTextField>
        {selection === "family" && families.length > 0 && (
          <StyledTextField
            select
            label="Family"
            name="family_id"
            value={formData.family_id}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            <MenuItem value="">Select a Family</MenuItem>
            {Object.values(families).map((family) => (
              <MenuItem key={family.id} value={family.id}>
                {family.family_profile?.family_name || "Unnamed Family"}
              </MenuItem>
            ))}
          </StyledTextField>
        )}
        {selection === "teacher" && teachers.length > 0 && (
          <StyledTextField
            select
            label="Teacher"
            name="teacher_id"
            value={formData.teacher_id}
            onChange={handleChange}
            fullWidth
            margin="normal"
          >
            <MenuItem value="">Select a Teacher</MenuItem>
            {teachers.map((teacher) => (
              <MenuItem key={teacher.id} value={teacher.id}>
                {teacher.teacher_profile?.full_name || "Unnamed Teacher"}
              </MenuItem>
            ))}
          </StyledTextField>
        )}
        {selection === "school" && schools.length > 0 && (
          <>
            <StyledTextField
              select
              label="School"
              name="school_id"
              value={formData.school_id}
              onChange={handleChange}
              fullWidth
              margin="normal"
            >
              <MenuItem value="">Select a School</MenuItem>
              {schools.map((school) => (
                <MenuItem key={school.id} value={school.id}>
                  {school.school_profile?.school_name || "Unnamed School"}
                </MenuItem>
              ))}
            </StyledTextField>
            {filteredTeachers.length > 0 && (
              <StyledTextField
                select
                label="Teacher"
                name="teacher_id"
                value={formData.teacher_id}
                onChange={handleChange}
                fullWidth
                margin="normal"
              >
                <MenuItem value="">Select a Teacher</MenuItem>
                {filteredTeachers.map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {teacher.teacher_profile?.full_name || "Unnamed Teacher"}
                  </MenuItem>
                ))}
              </StyledTextField>
            )}
          </>
        )}
        <Typography className="note">Fields marked with (*) are required.</Typography>
        <Button
          type="submit"
          className="primary-button"
          fullWidth
          sx={{ marginTop: "20px", padding: "10px" }}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Submit"}
        </Button>
      </form>

      <AlertModal
        open={alert.open}
        onClose={() => {
          setAlert((prev) => ({ ...prev, open: false }));
          if (alert.type === "success") {
            window.location.href = "/dashboard";
          }
        }}
        title={alert.title}
        message={alert.message}
        type={alert.type}
      />
    </>
  );
};

export default StudentForm;
